import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { usePermissionCatalog } from '../api'
import { permissionDisplayLabel, toggleMany } from '../permissions'

export function PermissionPicker({ value, onChange }: { value: string[]; onChange: (codes: string[]) => void }) {
  const { t } = useTranslation()
  const catalog = usePermissionCatalog().data
  const granted = useMemo(() => new Set(value), [value])

  const groups = catalog?.groups.length
    ? catalog.groups
    : (catalog?.modules ?? []).map((m) => ({
        key: m.key,
        title: m.title || m.key,
        description: m.description || '',
        sortOrder: m.sortOrder ?? 0,
        permissions: m.resources.flatMap((r) =>
          r.actions.map((action) => {
            const code = r.codes?.[action] ?? `${m.key}.${r.key}.${action}`
            return {
              id: catalog?.idByCode[code] ?? code,
              code,
              title: catalog?.titleByCode[code] || '',
              description: r.descriptions?.[action] || catalog?.descriptionByCode[code] || '',
              module: m.key,
            }
          }),
        ),
      }))

  return (
    <div className="space-y-3">
      {groups.map((group) => {
        const codes = group.permissions.map((p) => p.code)
        const count = codes.filter((c) => granted.has(c)).length
        const allOn = codes.length > 0 && count === codes.length
        return (
          <section key={group.key} className="overflow-hidden rounded-xl border">
            <header className="flex items-start gap-2 border-b bg-muted/40 px-3 py-2.5">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{group.title}</p>
                {group.description ? <p className="mt-0.5 text-xs text-muted-foreground">{group.description}</p> : null}
              </div>
              <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                {count}/{codes.length}
              </span>
              <button type="button" className="shrink-0 text-xs font-medium text-brand-blue hover:underline" onClick={() => onChange(toggleMany(value, codes))}>
                {allOn ? t('access.form.clearAll') : t('access.form.selectAll')}
              </button>
            </header>
            <ul className="divide-y">
              {group.permissions.map((p) => {
                const on = granted.has(p.code)
                const label = p.title || p.description || permissionDisplayLabel(p.code, catalog, p.code)
                return (
                  <li key={p.code}>
                    <label className="flex cursor-pointer items-start gap-3 px-3 py-2.5">
                      <input
                        type="checkbox"
                        className="mt-0.5 size-4 shrink-0 accent-[var(--color-primary)]"
                        checked={on}
                        onChange={() => onChange(toggleMany(value, [p.code]))}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium">{label}</span>
                        {p.description && p.description !== label ? (
                          <span className="mt-0.5 block text-xs text-muted-foreground">{p.description}</span>
                        ) : null}
                        <span className="mt-0.5 block font-mono text-[11px] text-muted-foreground">{p.code}</span>
                      </span>
                    </label>
                  </li>
                )
              })}
            </ul>
          </section>
        )
      })}
    </div>
  )
}
